import { useState, useEffect, useMemo } from 'react';
import { Calendar, ChevronLeft, ChevronRight, X, Check } from 'lucide-react';
import { formatDate } from '../../utils/formatters';
import useModalBack from '../../hooks/useModalBack';

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

const DAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

export default function DateInput({
  value,
  onChange,
  className = '',
  error = false,
  size = 'default',
  placeholder = 'DD-MM-YYYY',
  autoOpen = false,
  onClose,
  instantSelect = false,
  maxDate,  // YYYY-MM-DD — disable dates after this
  minDate   // YYYY-MM-DD — disable dates before this
}) {
  const [isOpen, setIsOpen] = useState(false);

  // Close picker helper
  const closePicker = () => {
    setIsOpen(false);
    if (onClose) onClose();
  };

  useModalBack(isOpen, closePicker, 'DateInput');

  // Auto-open support (e.g., auto-open "To" picker after "From" is selected)
  useEffect(() => {
    if (autoOpen && !isOpen) {
      setIsOpen(true);
    }
  }, [autoOpen]);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [selectedDate, setSelectedDate] = useState('');
  const [showYearPicker, setShowYearPicker] = useState(false);

  // Parse value on mount and when value changes (only when modal is closed)
  // This prevents overwriting user's selection while they're picking a date
  useEffect(() => {
    if (isOpen) return; // Don't sync while modal is open

    if (value) {
      setSelectedDate(value);
      const [year, month] = value.split('-').map(Number);
      if (year && month) {
        setCurrentMonth(month - 1);
        setCurrentYear(year);
      }
    } else {
      setSelectedDate('');
    }
  }, [value, isOpen]);

  // Convert YYYY-MM-DD to DD-MM-YYYY for display
  const displayValue = value ? formatDate(value, 'display') : '';

  // Get days in month
  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  // Get first day of month (0 = Sunday)
  const getFirstDayOfMonth = (year, month) => {
    return new Date(year, month, 1).getDay();
  };

  // Generate calendar days
  const calendarDays = useMemo(() => {
    const daysInMonth = getDaysInMonth(currentYear, currentMonth);
    const firstDay = getFirstDayOfMonth(currentYear, currentMonth);
    const daysInPrevMonth = getDaysInMonth(currentYear, currentMonth - 1);

    const days = [];

    // Previous month days
    for (let i = firstDay - 1; i >= 0; i--) {
      const day = daysInPrevMonth - i;
      const prevMonth = currentMonth === 0 ? 11 : currentMonth - 1;
      const prevYear = currentMonth === 0 ? currentYear - 1 : currentYear;
      days.push({
        day,
        month: prevMonth,
        year: prevYear,
        isCurrentMonth: false,
        date: `${prevYear}-${String(prevMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
      });
    }

    // Current month days
    for (let day = 1; day <= daysInMonth; day++) {
      days.push({
        day,
        month: currentMonth,
        year: currentYear,
        isCurrentMonth: true,
        date: `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
      });
    }

    // Next month days to fill the grid
    const remainingDays = 42 - days.length;
    for (let day = 1; day <= remainingDays; day++) {
      const nextMonth = currentMonth === 11 ? 0 : currentMonth + 1;
      const nextYear = currentMonth === 11 ? currentYear + 1 : currentYear;
      days.push({
        day,
        month: nextMonth,
        year: nextYear,
        isCurrentMonth: false,
        date: `${nextYear}-${String(nextMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
      });
    }

    return days;
  }, [currentMonth, currentYear]);

  // Check if date is today
  const isToday = (date) => {
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    return date === todayStr;
  };

  // Navigate months
  const goToPreviousMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const goToNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  // Handle date selection and confirm
  const handleDateSelect = (dayInfo) => {
    setSelectedDate(dayInfo.date);
    setCurrentMonth(dayInfo.month);
    setCurrentYear(dayInfo.year);
    // In instant mode, confirm immediately on tap
    if (instantSelect) {
      if (typeof onChange === 'function') {
        onChange(dayInfo.date);
      }
      closePicker();
    }
  };

  // Handle confirm
  const handleConfirm = () => {
    if (!selectedDate) return;

    if (typeof onChange === 'function') {
      onChange(selectedDate);
    }
    closePicker();
  };

  // Set to today
  const setToday = () => {
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    setSelectedDate(todayStr);
    setCurrentMonth(today.getMonth());
    setCurrentYear(today.getFullYear());
  };

  // Generate year options
  const yearOptions = useMemo(() => {
    const currentYearNow = new Date().getFullYear();
    const years = [];
    for (let y = currentYearNow - 10; y <= currentYearNow + 5; y++) {
      years.push(y);
    }
    return years;
  }, []);


  const sizeClasses = size === 'sm'
    ? 'h-[46px] px-3 pl-9 text-sm'
    : 'py-3 px-4 pl-10';

  const iconSizeClasses = size === 'sm'
    ? 'w-4 h-4 left-2.5'
    : 'w-5 h-5 left-3';

  return (
    <div className="relative">
      {/* Display Input */}
      <div
        className={`bg-surface-50 border border-surface-200 rounded-xl flex items-center cursor-pointer relative ${sizeClasses} ${error ? 'border-red-500' : ''} ${className}`}
        onClick={() => setIsOpen(true)}
      >
        <Calendar className={`absolute top-1/2 -translate-y-1/2 text-surface-400 pointer-events-none ${iconSizeClasses}`} />
        {displayValue || <span className="text-surface-400">{placeholder}</span>}
      </div>

      {/* Compact Modal */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={closePicker}>
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div
              className="flex items-center justify-between px-4 py-3"
              style={{
                background: `linear-gradient(135deg, var(--accent-bg), color-mix(in srgb, var(--accent-bg) 85%, black))`,
              }}
            >
              <h3 className="font-semibold text-sm" style={{ color: 'var(--accent-text)' }}>
                {placeholder && placeholder !== 'DD-MM-YYYY'
                  ? `Select "${placeholder}" Date`
                  : 'Select Date'}
              </h3>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={setToday}
                  className="px-2 py-1 text-xs font-medium rounded-lg bg-white/15 hover:bg-white/25 transition-all"
                  style={{ color: 'var(--accent-text)' }}
                >
                  Today
                </button>
                <button
                  type="button"
                  onClick={closePicker}
                  className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 transition-all"
                  style={{ color: 'var(--accent-text)' }}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-3">
              {/* Month/Year Navigation */}
              <div className="flex items-center justify-between mb-2">
                <button
                  type="button"
                  onClick={goToPreviousMonth}
                  className="p-1.5 rounded-lg hover:bg-surface-100 transition-colors"
                >
                  <ChevronLeft className="w-4 h-4 text-surface-600" />
                </button>

                <button
                  type="button"
                  onClick={() => setShowYearPicker(!showYearPicker)}
                  className="px-3 py-1 rounded-lg hover:bg-surface-100 transition-colors"
                >
                  <span className="font-semibold text-sm text-surface-900">
                    {MONTHS[currentMonth]} {currentYear}
                  </span>
                </button>

                <button
                  type="button"
                  onClick={goToNextMonth}
                  className="p-1.5 rounded-lg hover:bg-surface-100 transition-colors"
                >
                  <ChevronRight className="w-4 h-4 text-surface-600" />
                </button>
              </div>

              {/* Year Picker */}
              {showYearPicker && (
                <div className="mb-3 p-2 bg-surface-50 rounded-xl max-h-32 overflow-y-auto">
                  <div className="grid grid-cols-4 gap-1">
                    {yearOptions.map((year) => (
                      <button
                        key={year}
                        type="button"
                        onClick={() => {
                          setCurrentYear(year);
                          setShowYearPicker(false);
                        }}
                        className={`py-1.5 rounded-lg text-xs font-medium transition-all ${
                          year === currentYear
                            ? 'text-white'
                            : 'text-surface-600 hover:bg-surface-100'
                        }`}
                        style={year === currentYear ? { backgroundColor: 'var(--accent-bg)' } : {}}
                      >
                        {year}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Day Headers */}
              <div className="grid grid-cols-7 gap-0.5 mb-1">
                {DAYS.map((day, i) => (
                  <div key={i} className="text-center text-[10px] font-semibold text-surface-400 py-1">
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-0.5">
                {calendarDays.map((dayInfo, index) => {
                  const isSelected = dayInfo.date === selectedDate;
                  const isTodayDate = isToday(dayInfo.date);
                  const isDisabled = (maxDate && dayInfo.date > maxDate) || (minDate && dayInfo.date < minDate);

                  return (
                    <button
                      key={index}
                      type="button"
                      onClick={() => !isDisabled && handleDateSelect(dayInfo)}
                      disabled={isDisabled}
                      className={`aspect-square flex items-center justify-center rounded-lg text-xs font-medium transition-all ${
                        isDisabled
                          ? 'text-surface-200 cursor-not-allowed'
                          : isSelected
                          ? 'text-white'
                          : isTodayDate
                          ? 'ring-1 ring-offset-1 text-surface-900 hover:bg-surface-100'
                          : dayInfo.isCurrentMonth
                          ? 'text-surface-800 hover:bg-surface-100'
                          : 'text-surface-300'
                      }`}
                      style={{
                        backgroundColor: isSelected && !isDisabled ? 'var(--accent-bg)' : undefined,
                        '--tw-ring-color': isTodayDate && !isSelected ? 'var(--accent-bg)' : undefined
                      }}
                    >
                      {dayInfo.day}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Footer */}
            <div className="flex gap-2 p-3 pt-0">
              <button
                type="button"
                onClick={closePicker}
                className="flex-1 py-2.5 px-4 rounded-xl border border-surface-200 font-medium text-sm text-surface-700 hover:bg-surface-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={!selectedDate}
                className="flex-1 py-2.5 px-4 rounded-xl font-medium text-sm text-white transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5"
                style={{ backgroundColor: 'var(--accent-bg)' }}
              >
                <Check className="w-4 h-4" />
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
